/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, type ReactNode } from 'react';
import { useRecipeInteractions } from '../hooks/useRecipeInteractions';
import type { Collection } from '../hooks/useRecipeInteractions';
import { useAuth } from './AuthContext';
import { useActingEngagementProfileId } from '../hooks/useActingEngagementProfileId';
import { useRecipeEngagementWriteAccess } from '../hooks/useRecipeEngagementWriteAccess';

interface RecipeInteractionsContextType {
  /** False when acting as a family profile with viewer role — likes/saves/collection writes blocked in UI + handlers. */
  canWriteRecipeEngagement: boolean;
  toggleLike: (recipeId: string) => void;
  toggleSave: (recipeId: string) => void;
  isLiked: (recipeId: string) => boolean;
  isSaved: (recipeId: string) => boolean;
  likedCount: number;
  savedCount: number;
  likedRecipeIds: string[];
  savedRecipeIds: string[];
  collections: Collection[];
  createCollection: (name: string) => string;
  deleteCollection: (collectionId: string) => void | Promise<void>;
  addRecipeToCollection: (collectionId: string, recipeId: string) => void;
  removeRecipeFromCollection: (collectionId: string, recipeId: string) => void;
  updateCollectionName: (
    collectionId: string,
    newName: string,
  ) => void | Promise<void>;
  getCollection: (collectionId: string) => Collection | undefined;
  setLikedRecipes: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSavedRecipes: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const RecipeInteractionsContext = createContext<
  RecipeInteractionsContextType | undefined
>(undefined);

export const RecipeInteractionsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const actingProfileId = useActingEngagementProfileId();
  const canWriteRecipeEngagement = useRecipeEngagementWriteAccess();
  const interactions = useRecipeInteractions(
    !isLoading && isAuthenticated,
    actingProfileId,
    canWriteRecipeEngagement,
  );

  return (
    <RecipeInteractionsContext.Provider
      value={{ ...interactions, canWriteRecipeEngagement }}
    >
      {children}
    </RecipeInteractionsContext.Provider>
  );
};

export const useRecipeInteractionsContext = () => {
  const context = useContext(RecipeInteractionsContext);
  if (!context) {
    throw new Error(
      'useRecipeInteractionsContext must be used within RecipeInteractionsProvider',
    );
  }
  return context;
};
